import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getBoatTypeLayout } from '../../../utils/boatTypes';
import { useScheduleModalDragAndDrop } from '../../../hooks/useScheduleModalDragAndDrop';
import {
  getRiggingValidationWarnings,
  canAssignRowerToSeat,
  parseRiggingConfig,
  getExpectedSideForSeat,
} from '../../../utils/riggingValidation';
import EnhancedSeatSlot from './EnhancedSeatSlot';
import AvailabilityRowerCard from './AvailabilityRowerCard';

const CrewLineupEditor = ({
  crew,
  availableRowers,
  crewLineup,
  onLineupChange,
  assignedRowers,
  onShowAllChange,
  showAll = false,
}) => {
  const handleShowAllToggle = () => {
    const newShowAll = !showAll;
    if (onShowAllChange) {
      onShowAllChange(newShowAll);
    }
  };
  const {
    dropTarget,
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleRemoveMember,
  } = useScheduleModalDragAndDrop(onLineupChange);

  const boatType = getBoatTypeLayout(crew.boat_type);
  const { seats, hasCox } = boatType;
  const totalPositions = seats + (hasCox ? 1 : 0);

  const renderSeats = () => {
    const seatComponents = [];

    // Add cox position if boat has cox (seat 0)
    if (hasCox) {
      const coxInSeat = crewLineup[0];
      seatComponents.push(
        <EnhancedSeatSlot
          key={0}
          crew={crew}
          seat_number={0}
          rowerInSeat={
            coxInSeat
              ? {
                  user: coxInSeat,
                  seat_number: 0,
                }
              : null
          }
          isDropTarget={dropTarget === 0}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, 0, crew, crewLineup)}
          onRemoveRower={() => handleRemoveMember(crew, crewLineup, 0)}
          onDragStart={handleDragStart}
        />
      );
    }

    // Add regular rower seats (1 to seats)
    for (let i = 1; i <= seats; i++) {
      const arrayIndex = hasCox ? i : i - 1;
      const rowerInSeat = crewLineup[arrayIndex];
      seatComponents.push(
        <EnhancedSeatSlot
          key={i}
          crew={crew}
          seat_number={i}
          rowerInSeat={
            rowerInSeat
              ? {
                  user: rowerInSeat,
                  seat_number: i,
                }
              : null
          }
          isDropTarget={dropTarget === i}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, arrayIndex, crew, crewLineup)}
          onRemoveRower={() => handleRemoveMember(crew, crewLineup, arrayIndex)}
          onDragStart={handleDragStart}
        />
      );
    }
    return seatComponents;
  };

  // Validation functions
  const validateLineup = () => {
    const boatType = getBoatTypeLayout(crew.boat_type);
    const validations = {
      errors: [],
      warnings: [],
      info: [],
    };

    // Check capacity (excluding cox position)
    const filledPositions = crewLineup.filter(Boolean).length;
    const filledRowerPositions = boatType?.hasCox
      ? crewLineup.slice(1).filter(Boolean).length // Exclude cox position (index 0)
      : filledPositions;
    const totalRowerPositions = boatType?.seats || 0;

    if (filledRowerPositions < totalRowerPositions) {
      validations.warnings.push(
        `${totalRowerPositions - filledRowerPositions} rower position(s) still need to be filled`
      );
    }

    // Check side balance for non-scull boats
    if ((!crew.boat_type?.includes('x') || !crew.boat_type?.includes('*')) && boatType?.seats > 2) {
      const assignedRowers = crewLineup.filter(Boolean);
      const coxswain = boatType?.hasCox ? assignedRowers[0] : null;
      if (coxswain) {
        assignedRowers.shift(); // Remove coxswain from the lineup for side balance check
      }
      const portRowers = assignedRowers.filter((r) => r.preferred_side === 'port').length;
      const starboardRowers = assignedRowers.filter((r) => r.preferred_side === 'starboard').length;
      const noneboardRowers = assignedRowers.filter(
        (r) => !r.preferred_side || r.preferred_side === 'none'
      ).length;
      const balanceDiff = Math.abs(portRowers - starboardRowers);

      if (noneboardRowers > 0) {
        validations.info.push(`${noneboardRowers} rower(s) without side preference`);
      }

      if (balanceDiff > noneboardRowers) {
        validations.warnings.push(
          `Side imbalance: ${portRowers} port vs ${starboardRowers} starboard rowers`
        );
      }
    }

    // Check for cox position in boats that need one
    if (boatType?.hasCox && !crewLineup[0]) {
      validations.warnings.push('Coxswain position is not filled');
    }

    // Check availability of assigned rowers
    const assignedRowers = crewLineup.filter(Boolean);
    const availabilityIssues = [];

    assignedRowers.forEach((rower, index) => {
      const seatNumber = boatType?.hasCox ? index : index + 1;

      if (rower.availability_status === 'not_available') {
        const suffix = rower.availability_details?.declared_unavailable_for_session
          ? 'Marked unavailable for this session'
          : 'Not available for this session';
        availabilityIssues.push(
          `Seat ${seatNumber}: ${rower.first_name} ${rower.last_name} - ${suffix}`
        );
      } else if (rower.availability_status === 'partly_available') {
        const availablePeriods = rower.availability_details?.conflicts?.[0]?.available_periods;
        const periodsText = availablePeriods
          ? availablePeriods.map((p) => `${p.start_time}-${p.end_time}`).join(', ')
          : `${rower.availability_details?.available_minutes || 0} min`;

        availabilityIssues.push(
          `Seat ${seatNumber}: ${rower.first_name} ${rower.last_name} - Partly available (${periodsText})`
        );
      } else if (rower.availability_status === 'conflict') {
        const conflictSession = rower.availability_details?.conflicts?.[0];
        const conflictText = conflictSession
          ? `Conflict: ${conflictSession.session_name}`
          : 'Conflicting session';

        availabilityIssues.push(
          `Seat ${seatNumber}: ${rower.first_name} ${rower.last_name} - ${conflictText}`
        );
      }
    });

    if (availabilityIssues.length > 0) {
      validations.warnings.push(...availabilityIssues);
    }

    return validations;
  };

  // Get rigging validation warnings
  const riggingWarnings = getRiggingValidationWarnings(crew, crewLineup);

  // Debug rigging configuration
  console.log('Crew rigging config debug:', {
    crewId: crew.id,
    boatType: crew.boat_type,
    sidePreferenceStrict: crew.side_preference_strict,
    riggingConfig: crew.rigging_config,
    riggingWarningsCount: riggingWarnings.length,
    riggingWarnings,
  });

  const validations = validateLineup();

  const renderValidationSummary = () => {
    const allWarnings = [...validations.warnings];
    const allErrors = [...validations.errors];

    // Add rigging warnings and errors to the validation summary
    riggingWarnings.forEach((warning) => {
      if (warning.severity === 'error') {
        allErrors.push(`${warning.rowerName}: ${warning.message}`);
      } else {
        allWarnings.push(`Seat ${warning.seatNumber}: ${warning.rowerName} - ${warning.message}`);
      }
    });

    if (allErrors.length === 0 && allWarnings.length === 0 && validations.info.length === 0) {
      return null;
    }

    return (
      <div className="mt-4 space-y-2">
        {allErrors.map((error, index) => (
          <div
            key={`error-${index}`}
            className="flex items-center gap-2 rounded bg-red-50 p-2 text-sm text-red-600"
          >
            <span className="text-red-500">❌</span>
            {error}
          </div>
        ))}
        {allWarnings.map((warning, index) => (
          <div
            key={`warning-${index}`}
            className="flex items-center gap-2 rounded bg-amber-50 p-2 text-sm text-amber-600"
          >
            <span className="text-amber-500">⚠️</span>
            {warning}
          </div>
        ))}
        {validations.info.map((info, index) => (
          <div
            key={`info-${index}`}
            className="flex items-center gap-2 rounded bg-blue-50 p-2 text-sm text-blue-600"
          >
            <span className="text-blue-500">ℹ️</span>
            {info}
          </div>
        ))}
      </div>
    );
  };

  console.log('Crew Lineup Editor Rendered', {
    crewId: crew.id,
    crewLineup,
    availableRowersCount: availableRowers.length,
    assignedRowersCount: assignedRowers.size,
    totalPositions,
    validations,
  });

  const unassignedRowers = availableRowers.filter((rower) => {
    // Filter out rowers already assigned to lineups in this session
    if (assignedRowers.has(rower.id)) {
      return false;
    }

    // Filter by crew membership
    if (!rower.crew_memberships.includes(crew.id)) {
      return false;
    }

    // If showAll is false, only show fully available rowers
    if (!showAll) {
      return rower.availability_status === 'fully_available';
    }

    // If showAll is true, show all rowers regardless of availability status
    return true;
  });

  // Smart assignment functions
  const smartAssign = () => {
    // Check if there are available rowers
    if (unassignedRowers.length === 0) {
      console.warn('Cannot perform smart assignment: No available rowers');
      return;
    }

    // Check if rigging config is required but missing
    if (crew.side_preference_strict && (!crew.rigging_config || crew.rigging_config === '{}')) {
      console.warn(
        'Cannot perform smart assignment: Rigging configuration is required for sweep boats'
      );
      return;
    }

    // Get unassigned rowers for this crew, filtering to only fully available rowers
    const unassigned = availableRowers.filter((r) => {
      return (
        !assignedRowers.has(r.id) &&
        r.crew_memberships.includes(crew.id) &&
        r.availability_status === 'fully_available'
      );
    });

    const boatType = getBoatTypeLayout(crew.boat_type);
    const totalPositions = (boatType?.seats || 0) + (boatType?.hasCox ? 1 : 0);
    const lineup = new Array(totalPositions).fill(null);

    // For sculling boats, just fill in order
    if (crew.boat_type?.includes('x')) {
      unassigned.slice(0, totalPositions).forEach((rower, idx) => {
        lineup[idx] = {
          id: rower.id,
          name: `${rower.first_name} ${rower.last_name}`,
          first_name: rower.first_name,
          last_name: rower.last_name,
          preferred_side: rower.preferred_side,
          availability_status: rower.availability_status,
          availability_details: rower.availability_details,
        };
      });
    } else {
      // For sweep boats, try to balance sides
      let availableForSeats = [...unassigned]; // Create a copy to track available rowers

      // Fill cox position first if exists and remove from available pool
      if (boatType?.hasCox && availableForSeats.length > 0) {
        const coxRower = availableForSeats[0];
        lineup[0] = {
          id: coxRower.id,
          name: `${coxRower.first_name} ${coxRower.last_name}`,
          first_name: coxRower.first_name,
          last_name: coxRower.last_name,
          preferred_side: coxRower.preferred_side,
          availability_status: coxRower.availability_status,
          availability_details: coxRower.availability_details,
        };
        // Remove cox from available rowers
        availableForSeats = availableForSeats.slice(1);
      }

      // Now categorize remaining rowers by side preference
      const portRowers = availableForSeats.filter((r) => r.preferred_side === 'port');
      const starboardRowers = availableForSeats.filter((r) => r.preferred_side === 'starboard');
      const noPreferenceRowers = availableForSeats.filter((r) => {
        const pref = r.preferred_side;
        return !pref || pref === 'none';
      });

      let portIndex = 0;
      let starboardIndex = 0;
      let noPreferenceIndex = 0;

      // Fill rower positions based on side preferences
      for (let seat = 1; seat <= boatType?.seats; seat++) {
        const arrayIndex = boatType?.hasCox ? seat : seat - 1;

        // Get expected side from rigging configuration
        let expectedSide = null;

        // For strict side preference crews, rigging config is required
        if (crew.side_preference_strict) {
          if (!crew.rigging_config) {
            console.warn(`Crew ${crew.id} has strict side preference but no rigging config`);
            continue; // Skip this seat
          }

          const riggingConfig = parseRiggingConfig(crew.rigging_config);
          if (!riggingConfig || Object.keys(riggingConfig).length === 0) {
            console.warn(`Crew ${crew.id} has strict side preference but empty rigging config`);
            continue; // Skip this seat
          }

          expectedSide = getExpectedSideForSeat(riggingConfig, seat);
          if (!expectedSide) {
            console.warn(
              `Crew ${crew.id} has strict side preference but no expected side for seat ${seat}`
            );
            continue; // Skip this seat
          }
        } else {
          // For non-strict crews, use rigging config if available
          if (crew.rigging_config) {
            const riggingConfig = parseRiggingConfig(crew.rigging_config);
            expectedSide = getExpectedSideForSeat(riggingConfig, seat);
          }

          // Fallback to alternating pattern only for non-strict crews
          if (!expectedSide) {
            expectedSide = seat % 2 === 1 ? 'starboard' : 'port';
          }
        }

        let selectedRower = null;

        if (expectedSide === 'port' && portIndex < portRowers.length) {
          selectedRower = portRowers[portIndex++];
        } else if (expectedSide === 'starboard' && starboardIndex < starboardRowers.length) {
          selectedRower = starboardRowers[starboardIndex++];
        } else if (noPreferenceIndex < noPreferenceRowers.length) {
          selectedRower = noPreferenceRowers[noPreferenceIndex++];
        } else if (expectedSide === 'port' && starboardIndex < starboardRowers.length) {
          selectedRower = starboardRowers[starboardIndex++];
        } else if (expectedSide === 'starboard' && portIndex < portRowers.length) {
          selectedRower = portRowers[portIndex++];
        }

        if (selectedRower) {
          lineup[arrayIndex] = {
            id: selectedRower.id,
            name: `${selectedRower.first_name} ${selectedRower.last_name}`,
            first_name: selectedRower.first_name,
            last_name: selectedRower.last_name,
            preferred_side: selectedRower.preferred_side,
            availability_status: selectedRower.availability_status,
            availability_details: selectedRower.availability_details,
          };
        }
      }
    }

    onLineupChange(crew.id, lineup);
  };

  // Auto-fill remaining empty positions while preserving existing assignments
  const autoFillRemaining = () => {
    // Check if there are available rowers
    if (unassignedRowers.length === 0) {
      console.warn('Cannot perform auto-fill: No available rowers');
      return;
    }

    // Check if rigging config is required but missing
    if (crew.side_preference_strict && (!crew.rigging_config || crew.rigging_config === '{}')) {
      console.warn('Cannot perform auto-fill: Rigging configuration is required for sweep boats');
      return;
    }

    // Get unassigned rowers for this crew, filtering to only fully available rowers
    const unassigned = availableRowers.filter((r) => {
      return (
        !assignedRowers.has(r.id) &&
        r.crew_memberships.includes(crew.id) &&
        r.availability_status === 'fully_available'
      );
    });

    if (unassigned.length === 0) {
      console.warn('No available rowers to auto-fill');
      return;
    }

    const boatType = getBoatTypeLayout(crew.boat_type);
    const totalPositions = (boatType?.seats || 0) + (boatType?.hasCox ? 1 : 0);

    // Create a copy of the current lineup to preserve existing assignments
    const newLineup = [...crewLineup];

    // Find empty positions that need to be filled
    const emptyPositions = [];
    for (let i = 0; i < totalPositions; i++) {
      if (!newLineup[i]) {
        emptyPositions.push(i);
      }
    }

    if (emptyPositions.length === 0) {
      console.log('No empty positions to fill');
      return;
    }

    // For sculling boats, just fill empty positions in order
    if (crew.boat_type?.includes('x')) {
      for (const position of emptyPositions) {
        if (unassigned.length > 0) {
          const rower = unassigned.shift(); // Remove and get the first available rower
          newLineup[position] = {
            id: rower.id,
            name: `${rower.first_name} ${rower.last_name}`,
            first_name: rower.first_name,
            last_name: rower.last_name,
            preferred_side: rower.preferred_side,
          };
        }
      }
    } else {
      // For sweep boats, fill based on rigging configuration

      for (const position of emptyPositions) {
        if (unassigned.length === 0) break;

        // Convert array position to seat number
        const seatNumber = boatType?.hasCox ? position : position + 1;

        // Get expected side from rigging configuration
        let expectedSide = null;

        // For strict side preference crews, rigging config is required
        if (crew.side_preference_strict) {
          if (!crew.rigging_config) {
            console.warn(`Crew ${crew.id} has strict side preference but no rigging config`);
            continue; // Skip this position
          }

          const riggingConfig = parseRiggingConfig(crew.rigging_config);
          if (!riggingConfig || Object.keys(riggingConfig).length === 0) {
            console.warn(`Crew ${crew.id} has strict side preference but empty rigging config`);
            continue; // Skip this position
          }

          expectedSide = getExpectedSideForSeat(riggingConfig, seatNumber);
          if (!expectedSide) {
            console.warn(
              `Crew ${crew.id} has strict side preference but no expected side for seat ${seatNumber}`
            );
            continue; // Skip this position
          }
        } else {
          // For non-strict crews, use rigging config if available
          if (crew.rigging_config) {
            const riggingConfig = parseRiggingConfig(crew.rigging_config);
            expectedSide = getExpectedSideForSeat(riggingConfig, seatNumber);
          }

          // Fallback to alternating pattern only for non-strict crews
          if (!expectedSide) {
            expectedSide = seatNumber % 2 === 1 ? 'starboard' : 'port';
          }
        }

        // Find the best available rower for this position
        let selectedRower = null;

        // First try to find a rower with matching side preference
        for (let i = unassignedIndex; i < unassigned.length; i++) {
          const rower = unassigned[i];
          const rowerSide = rower.preferred_side;

          if (rowerSide === expectedSide) {
            selectedRower = rower;
            // Remove this rower from the unassigned list
            unassigned.splice(i, 1);
            break;
          }
        }

        // If no matching side preference, take the next available rower
        if (!selectedRower && unassigned.length > 0) {
          selectedRower = unassigned.shift(); // Remove and get the first available rower
        }

        if (selectedRower) {
          newLineup[position] = {
            id: selectedRower.id,
            name: `${selectedRower.first_name} ${selectedRower.last_name}`,
            first_name: selectedRower.first_name,
            last_name: selectedRower.last_name,
            preferred_side: selectedRower.preferred_side,

            availability_status: selectedRower.availability_status,
            availability_details: selectedRower.availability_details,
          };
        }
      }
    }

    onLineupChange(crew.id, newLineup);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Boat Seats */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h5 className="text-sm font-medium text-gray-700">
              Boat Positions ({totalPositions} positions)
            </h5>
            {validations.errors.length > 0 && (
              <span className="rounded bg-red-100 px-2 py-1 text-xs text-red-700">
                {validations.errors.length} error(s)
              </span>
            )}
            {validations.warnings.length > 0 && validations.errors.length === 0 && (
              <span className="rounded bg-amber-100 px-2 py-1 text-xs text-amber-700">
                {validations.warnings.length} warning(s)
              </span>
            )}
          </div>
          <div className="space-y-2">{renderSeats()}</div>
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <span>
              {crewLineup.filter(Boolean).length} / {totalPositions} positions filled
            </span>
            {crewLineup.filter(Boolean).length === totalPositions && (
              <span className="font-medium text-green-600">✓ Complete</span>
            )}
          </div>
        </div>

        {/* Available Rowers */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h5 className="text-sm font-medium text-gray-700">
              Available Rowers ({unassignedRowers.length})
            </h5>
            <div className="flex gap-1">
              <button
                onClick={handleShowAllToggle}
                className={`rounded border px-2 py-1 text-xs ${
                  showAll
                    ? 'border-gray-600 bg-gray-600 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                title="Toggle to show all rowers including those with limited availability"
              >
                {showAll ? 'Show Available Only' : 'Show All'}
              </button>
              <button
                onClick={smartAssign}
                disabled={unassignedRowers.length === 0}
                className={`rounded px-2 py-1 text-xs ${
                  unassignedRowers.length === 0
                    ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                    : !crewLineup.some(Boolean)
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'cursor-not-allowed bg-gray-300 text-gray-500'
                }`}
                title={
                  unassignedRowers.length === 0
                    ? 'No available rowers to assign'
                    : !crewLineup.some(Boolean)
                      ? 'Smart assign based on side preferences'
                      : 'Clear existing assignments first'
                }
              >
                Smart Fill
              </button>
            </div>
          </div>
          <div className="max-h-80 space-y-2 overflow-y-auto pr-2">
            {unassignedRowers.map((rower) => (
              <AvailabilityRowerCard
                key={rower.id}
                rower={rower}
                onDragStart={handleDragStart}
                isAssigned={false}
                showAvailabilityStatus={showAll}
              />
            ))}
            {unassignedRowers.length === 0 && (
              <div className="py-8 text-center text-gray-500">
                <div className="mb-2 text-lg">🚣‍♀️</div>
                <div className="text-sm">All rowers assigned</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 border-t border-gray-100 pt-3">
        <button
          onClick={() => {
            const emptyLineup = new Array(totalPositions).fill(null);
            onLineupChange(crew.id, emptyLineup);
          }}
          className="rounded border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
        >
          Clear All
        </button>
        <button
          onClick={autoFillRemaining}
          disabled={unassignedRowers.length === 0}
          className={`rounded px-3 py-1 text-xs ${
            unassignedRowers.length === 0
              ? 'cursor-not-allowed bg-gray-300 text-gray-500'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
          title={
            unassignedRowers.length === 0
              ? 'No available rowers to assign'
              : 'Auto-fill remaining empty positions'
          }
        >
          Auto-Fill Remaining
        </button>
      </div>

      {/* Validation Summary */}
      {renderValidationSummary()}
    </div>
  );
};

CrewLineupEditor.propTypes = {
  crew: PropTypes.object.isRequired,
  availableRowers: PropTypes.array.isRequired,
  crewLineup: PropTypes.array.isRequired,
  onLineupChange: PropTypes.func.isRequired,
  assignedRowers: PropTypes.instanceOf(Set).isRequired,
  onShowAllChange: PropTypes.func,
  showAll: PropTypes.bool,
};

export default CrewLineupEditor;
